import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight, BellRing, Briefcase, CalendarCheck, CalendarX2, Download, Eye, Globe, School, Send, Smartphone,
  UserPlus, Users,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "./api";
import { Badge, CarteIcone, Cellule, EnTeteMenu, Puces, Recherche, Section, Tableau, TONS_BLOC } from "./ui";
import { Encart } from "./DashboardPlus";
import { AnnoncesApplication, ComptesTest } from "./Application";
import { Simulateur } from "./Simulateur";
import { useConfirmation } from "./Confirmation";

/* Menu « Application » (école, 20.10.2026) : « refaire un menu application — les
   statistiques, qui a téléchargé l'application, l'envoi des notifications, la vision de
   l'application depuis n'importe quel compte, et un historique qui montre qui a fait quoi
   sur les apps. Le meilleur menu, super userfriendly, aligné sur le menu Assistant IA. »

   Même construction qu'Assistant IA : un bandeau à la couleur du menu (rose, comme la
   section Application du tableau de bord), puis des onglets. */

const VUES = [
  { valeur: "stats", label: "Statistiques" },
  { valeur: "qui", label: "Qui a l'app" },
  { valeur: "notifications", label: "Annonces & notifications" },
  { valeur: "voir", label: "Voir l'app" },
  { valeur: "historique", label: "Historique" },
];
const T = TONS_BLOC.application;
const ROSE_CLAIR = "#F9A8D4";
const COULEUR_PROFS = TONS_BLOC.profs.accent;
const COULEUR_ECOLE = TONS_BLOC.assistant.accent;
const ETIQUETTE_FILTRE = "text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted)]";
const ZURICH = "Europe/Zurich";
const nombre = (n) => (n || 0).toLocaleString("fr-CH");
const pourcent = (n, total) => (total ? `${Math.round((100 * (n || 0)) / total)} %` : "—");
const aller = (onglet, id) => window.dispatchEvent(new CustomEvent("console-aller", { detail: { onglet, id } }));
const jourZ = (d) => d.toLocaleDateString("en-CA", { timeZone: ZURICH });

/* « aujourd'hui à 18:05 », « hier à 07:40 », « il y a 5 jours » — heure suisse. */
function quand(iso) {
  const d = new Date(iso || "");
  if (!iso || Number.isNaN(d.getTime())) return "";
  const jours = Math.round((Date.parse(jourZ(new Date())) - Date.parse(jourZ(d))) / 86400000);
  const h = d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit", timeZone: ZURICH });
  if (jours <= 0) return `aujourd'hui à ${h}`;
  if (jours === 1) return `hier à ${h}`;
  if (jours < 30) return `il y a ${jours} jours`;
  return `le ${d.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: ZURICH })}`;
}
const dateCH = (iso) => {
  const d = new Date(String(iso || "").length === 10 ? `${iso}T12:00:00` : iso || "");
  return !iso || Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: ZURICH });
};

const Chargement = () => <p className="text-sm text-[var(--muted)]">Chargement…</p>;
const Erreur = ({ children }) => <p className="text-sm" style={{ color: "#B91C1C" }}>{children}</p>;

/* La barre des filtres, comme dans Assistant IA → Statistiques. */
const BarreFiltres = ({ children }) => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-2xl bg-white border px-4 py-3"
    style={{ borderColor: "var(--border)" }}>
    {children}
  </div>
);
const Filtre = ({ label, children }) => (
  <div className="flex flex-wrap items-center gap-2 min-w-0">
    <span className={ETIQUETTE_FILTRE}>{label}</span>
    {children}
  </div>
);
const PERIODES = [7, 30, 90, 365].map((j) => ({ valeur: j, label: j === 365 ? "1 an" : `${j} jours` }));

export default function MenuApplication({ cible }) {
  const depart = String(cible || "").split(":")[0];
  const [vue, setVue] = useState(VUES.some((v) => v.valeur === depart) ? depart : "stats");
  const [resume, setResume] = useState(null);
  useEffect(() => { api("/application/stats?jours=30").then(setResume).catch(() => {}); }, []);
  const e = resume?.entonnoir;

  return (
    <div className="space-y-6" data-testid="console-menu-application">
      <EnTeteMenu ton="application" Icon={Smartphone} contexte="Familles · profs · école" titre="Application"
        testid="application-entete"
        texte="Sur le téléphone des familles, des profs et de l'école."
        droite={e && (
          <>
            <Badge ton="vert" testid="application-badge-installee">{nombre(e.installees)} famille{e.installees > 1 ? "s ont" : " a"} installé l'app</Badge>
            <Badge ton="bleu" testid="application-badge-actives">{nombre(e.actives)} active{e.actives > 1 ? "s" : ""} ces 30 jours</Badge>
          </>
        )} />
      <Puces options={VUES} valeur={vue} onChange={setVue} testid="application-vues" />
      {vue === "stats" && <Statistiques onVoir={setVue} />}
      {vue === "qui" && <QuiALApp />}
      {vue === "notifications" && <Notifications />}
      {vue === "voir" && (
        <div className="space-y-10">
          <Simulateur />
          <ComptesTest />
        </div>
      )}
      {vue === "historique" && <Historique />}
    </div>
  );
}

// ── Statistiques ─────────────────────────────────────────────────────────────────────

function Statistiques({ onVoir }) {
  const [jours, setJours] = useState(30);
  const [s, setS] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => { api(`/application/stats?jours=${jours}`).then(setS).catch((x) => setErreur(x.message)); }, [jours]);
  if (!s) return erreur ? <Erreur>{erreur}</Erreur> : <Chargement />;
  const e = s.entonnoir;
  const periode = jours === 365 ? "cette année" : `ces ${jours} jours`;
  return (
    <div className="space-y-5" data-testid="application-stats">
      <BarreFiltres>
        <Filtre label="Période">
          <Puces options={PERIODES} valeur={jours} onChange={setJours} testid="application-periode" />
        </Filtre>
      </BarreFiltres>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <CarteIcone titre="Ont l'app" valeur={nombre(e.installees)} Icon={Download} teinte="rose"
          detail={`${pourcent(e.installees, e.familles)} des ${nombre(e.familles)} familles de la saison`}
          onClick={() => onVoir("qui")} testid="application-stat-installees" />
        <CarteIcone titre="Familles actives" valeur={nombre(e.actives)} Icon={Users} teinte="violet"
          detail={`venues ${periode} (app ou site)`} testid="application-stat-actives" />
        <CarteIcone titre="Nouveaux comptes" valeur={nombre(s.nouveaux_comptes)} Icon={UserPlus} teinte="vert"
          detail={`créés ${periode}`} testid="application-stat-nouveaux" />
        <CarteIcone titre="Jours d'utilisation" valeur={s.jours_moyens ? String(s.jours_moyens).replace(".", ",") : "—"}
          Icon={CalendarCheck} teinte="bleu" detail="en moyenne, par famille active" testid="application-stat-jours" />
        <CarteIcone titre="Absences annoncées" valeur={nombre(s.absences_app)} Icon={CalendarX2} teinte="ambre"
          detail={`dans l'app ${periode}`}
          onClick={() => onVoir("historique")} testid="application-stat-absences" />
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <Encart titre="Adoption" className="lg:col-span-2" testid="application-entonnoir"
          aide="Des familles inscrites aux familles qui s'en servent. Chaque barre se compare aux familles inscrites.">
          <Entonnoir e={e} periode={periode} />
        </Encart>
        <Encart titre="Ouvertures par jour" className="lg:col-span-3" testid="application-par-jour"
          aide="Une personne compte une fois par jour, même si elle ouvre l'app dix fois.">
          <OuverturesParJour serie={s.par_jour} depuis={s.releve_depuis} />
        </Encart>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <Encart titre="Dernières ouvertures" className="lg:col-span-3" testid="application-dernieres"
          aide="Les familles qui ont ouvert l'app ou leur espace le plus récemment."
          droite={(
            <button className="inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap" style={{ color: T.accent }}
              onClick={() => onVoir("qui")} data-testid="application-voir-qui">
              Toutes les familles <ArrowRight size={13} />
            </button>
          )}>
          {s.dernieres.length === 0 ? <p className="text-sm text-[var(--muted)]">Personne n'a encore ouvert l'app.</p> : (
            <ul className="divide-y" style={{ borderColor: "var(--border-soft)" }}>
              {s.dernieres.map((d, i) => (
                <li key={i} className="py-2.5 flex items-center gap-3 text-sm">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: T.doux, color: T.accent }}>
                    {d.mode === "app" ? <Smartphone size={15} /> : <Globe size={15} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold truncate">{d.nom}</span>
                    {d.enfants.length > 0 && <span className="block text-xs text-[var(--muted)] truncate">{d.enfants.join(", ")}</span>}
                  </span>
                  <span className="text-xs text-[var(--muted)] whitespace-nowrap">{quand(d.quand)} · {d.mode === "app" ? "app" : "site"}</span>
                </li>
              ))}
            </ul>
          )}
        </Encart>
        <div className="lg:col-span-2 grid gap-4 content-start">
          <CarteIcone titre="Profs" valeur={`${nombre(s.profs.actifs)} / ${nombre(s.profs.total)}`} Icon={Briefcase}
            teinte="orange" detail={`ont ouvert leur espace ${periode} · ${nombre(s.profs.connectes)} l'ont déjà utilisé`}
            onClick={() => onVoir("qui")} testid="application-stat-profs" />
          <CarteIcone titre="École (mode école)" valeur={`${nombre(s.ecole.actifs)} / ${nombre(s.ecole.total)}`} Icon={School}
            teinte="indigo" detail={`comptes venus ${periode} · ${nombre(s.ecole.telephones)} téléphone(s) reçoivent les notifications`}
            onClick={() => onVoir("notifications")} testid="application-stat-ecole" />
        </div>
      </div>
    </div>
  );
}

function Entonnoir({ e, periode }) {
  const etapes = [
    { label: "Familles inscrites cette saison", n: e.familles, couleur: "#CBD5E1" },
    { label: "Ont créé un compte", n: e.comptes, couleur: ROSE_CLAIR },
    { label: "Adresse prouvée (code ou Google)", n: e.prouves, couleur: "#F472B6" },
    { label: "App installée sur le téléphone", n: e.installees, couleur: T.accent },
    { label: `Actives ${periode}`, n: e.actives, couleur: "#9D174D" },
  ];
  return (
    <div className="space-y-3">
      {etapes.map((x) => (
        <div key={x.label}>
          <div className="flex items-baseline gap-2 text-sm">
            <span className="min-w-0 flex-1 truncate">{x.label}</span>
            <span className="font-semibold">{nombre(x.n)}</span>
            <span className="text-xs text-[var(--muted)] w-11 text-right">{pourcent(x.n, e.familles)}</span>
          </div>
          <div className="h-2.5 rounded-full mt-1 overflow-hidden" style={{ background: "#F1F5F9" }}>
            <div className="h-full rounded-full" style={{ width: `${e.familles ? Math.min(100, (100 * x.n) / e.familles) : 0}%`, background: x.couleur }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function InfobulleJour({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-xl bg-white border px-3 py-2 text-xs shadow-sm" style={{ borderColor: "var(--border)" }}>
      <div className="font-semibold mb-1">{dateCH(label)}</div>
      <div>Familles : {d.familles} ({d.familles_app} dans l'app)</div>
      <div>Profs : {d.profs} · École : {d.ecole}</div>
    </div>
  );
}

function OuverturesParJour({ serie, depuis }) {
  const donnees = serie.map((d) => ({ ...d, site: d.familles - d.familles_app }));
  const vide = !serie.some((d) => d.familles || d.profs || d.ecole);
  return (
    <div>
      <div style={{ height: 210 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={donnees} margin={{ top: 8, right: 4, left: -18, bottom: 0 }} barCategoryGap="18%">
            <CartesianGrid vertical={false} stroke="#EEF2F6" />
            <XAxis dataKey="jour" tickFormatter={(j) => dateCH(j).slice(0, 5)} interval="preserveStartEnd" minTickGap={24}
              tickLine={false} axisLine={{ stroke: "#CBD5E1" }} tick={{ fontSize: 11, fill: "#64748B" }} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={44} tick={{ fontSize: 11, fill: "#64748B" }} />
            <Tooltip cursor={{ fill: "rgba(219,39,119,0.06)" }} content={<InfobulleJour />} isAnimationActive={false} />
            <Bar dataKey="familles_app" stackId="o" fill={T.accent} maxBarSize={18} isAnimationActive={false} />
            <Bar dataKey="site" stackId="o" fill={ROSE_CLAIR} maxBarSize={18} isAnimationActive={false} />
            <Bar dataKey="profs" stackId="o" fill={COULEUR_PROFS} maxBarSize={18} isAnimationActive={false} />
            <Bar dataKey="ecole" stackId="o" fill={COULEUR_ECOLE} radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-[var(--muted)] mt-2">
        {[[T.accent, "Familles, app installée"], [ROSE_CLAIR, "Familles, navigateur"], [COULEUR_PROFS, "Profs"], [COULEUR_ECOLE, "École"]].map(([c, l]) => (
          <span key={l} className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: c }} />{l}</span>
        ))}
        <span className="ml-auto" data-testid="application-releve-depuis">
          {vide ? "Le relevé jour par jour commence aujourd'hui." : depuis ? `Relevé tenu depuis le ${dateCH(depuis)}.` : ""}
        </span>
      </div>
    </div>
  );
}

// ── Qui a l'app ──────────────────────────────────────────────────────────────────────

const ETATS_FAMILLE = {
  app: { label: "App installée", ton: "vert" },
  site: { label: "Navigateur seulement", ton: "bleu" },
  a_prouver: { label: "Adresse à prouver", ton: "ambre" },
  sans_compte: { label: "Pas de compte", ton: "neutre" },
};
const ETATS_PERSONNE = {
  app: { label: "App installée", ton: "vert" },
  site: { label: "Navigateur", ton: "bleu" },
  jamais: { label: "Jamais ouvert", ton: "neutre" },
  ferme: { label: "Accès fermé", ton: "rouge" },
};
const simple = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

function Derniere({ iso, mode }) {
  if (!iso) return <span className="text-[var(--muted)]">—</span>;
  return (
    <span className="whitespace-nowrap">
      {quand(iso)}
      {mode && <span className="text-[11px] text-[var(--muted)]"> · {mode === "app" ? "app" : "site"}</span>}
    </span>
  );
}

function QuiALApp() {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [groupe, setGroupe] = useState("familles");
  const [etat, setEtat] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { api("/application/qui").then(setD).catch((x) => setErreur(x.message)); }, []);
  const lignes = useMemo(() => {
    if (!d) return [];
    const mots = simple(q).split(/\s+/).filter(Boolean);
    return (d[groupe] || []).filter((x) => (!etat || x.etat === etat)
      && mots.every((m) => simple(`${x.nom} ${(x.enfants || []).join(" ")} ${x.email}`).includes(m)));
  }, [d, groupe, etat, q]);
  if (!d) return erreur ? <Erreur>{erreur}</Erreur> : <Chargement />;
  const compte = (cle) => d.familles.filter((f) => f.etat === cle).length;

  return (
    <div className="space-y-4" data-testid="application-qui">
      <BarreFiltres>
        <Filtre label="Qui">
          <Puces valeur={groupe} onChange={(g) => { setGroupe(g); setEtat(""); }} testid="application-qui-groupes"
            options={[{ valeur: "familles", label: "Familles", nb: d.familles.length },
                      { valeur: "profs", label: "Profs", nb: d.profs.length },
                      { valeur: "ecole", label: "École", nb: d.ecole.length }]} />
        </Filtre>
        {groupe === "familles" && (
          <Filtre label="Où en est-elle">
            <Puces valeur={etat} onChange={setEtat} testid="application-qui-etats"
              options={[{ valeur: "", label: "Toutes" },
                        ...Object.entries(ETATS_FAMILLE).map(([k, v]) => ({ valeur: k, label: v.label, nb: compte(k), testid: `application-qui-etat-${k}` }))]} />
          </Filtre>
        )}
        <div className="ml-auto"><Recherche valeur={q} onChange={setQ} largeur={260} testid="application-qui-recherche"
          placeholder={groupe === "familles" ? "Parent, enfant, adresse…" : "Nom…"} /></div>
      </BarreFiltres>

      {groupe === "familles" && etat === "sans_compte" && (
        <div className="rounded-2xl px-5 py-4 text-sm flex flex-wrap items-center gap-3" data-testid="application-inviter"
          style={{ background: T.doux, border: `1px solid ${T.bord}`, color: T.texte }}>
          <span className="flex-1 min-w-[14rem]">
            Ces familles n'ont pas encore de compte. Envoie le mode d'emploi aux familles qui n'ont pas l'app :
            l'envoi s'ouvre prêt (modèle « Application 1000 ARTS : mode d'emploi », e-mail seulement, sans les
            familles qui l'ont déjà) — il reste à relire et envoyer.
          </span>
          <button className="btn-primary text-sm" onClick={() => aller("envois", "invitation-app")} data-testid="application-ouvrir-envois">
            <Send size={14} /> Préparer l'invitation
          </button>
        </div>
      )}

      {groupe === "familles" && (
        <Tableau testid="application-qui-familles" lignes={lignes.slice(0, 300).map((f) => ({ ...f, id: f.email }))}
          vide="Aucune famille ne correspond."
          entetes={["Famille", "Où elle en est", "Dernière ouverture", { label: "Jours d'utilisation", droite: true }, "Compte créé"]}
          rendu={(f) => (
            <>
              <Cellule fort>
                {f.nom || f.email}
                <div className="text-xs font-normal text-[var(--muted)]">{[f.enfants.join(", "), f.nom ? f.email : ""].filter(Boolean).join(" · ")}</div>
              </Cellule>
              <Cellule><Badge ton={ETATS_FAMILLE[f.etat].ton}>{ETATS_FAMILLE[f.etat].label}</Badge></Cellule>
              <Cellule><Derniere iso={f.dernier_passage} mode={f.dernier_mode} /></Cellule>
              <Cellule droite>{f.jours_actifs || <span className="text-[var(--muted)]">—</span>}</Cellule>
              <Cellule muet>{dateCH(f.cree_le) || "—"}</Cellule>
            </>
          )} />
      )}
      {groupe === "profs" && (
        <Tableau testid="application-qui-profs" lignes={lignes} vide="Aucun prof ne correspond."
          entetes={["Prof", "Son espace prof", "Dernière ouverture", { label: "Jours d'utilisation", droite: true }]}
          rendu={(p) => (
            <>
              <Cellule fort>{p.nom}<div className="text-xs font-normal text-[var(--muted)]">{p.email || "pas d'adresse e-mail : ne peut pas se connecter"}</div></Cellule>
              <Cellule><Badge ton={ETATS_PERSONNE[p.etat].ton}>{ETATS_PERSONNE[p.etat].label}</Badge></Cellule>
              <Cellule><Derniere iso={p.dernier_passage} mode={p.dernier_mode} /></Cellule>
              <Cellule droite>{p.jours_actifs || <span className="text-[var(--muted)]">—</span>}</Cellule>
            </>
          )} />
      )}
      {groupe === "ecole" && (
        <Tableau testid="application-qui-ecole" lignes={lignes.map((u) => ({ ...u, id: u.email }))} vide="Aucun compte ne correspond."
          entetes={["Compte de l'école", "Mode école", "Dernière ouverture", { label: "Notifications", droite: true }]}
          rendu={(u) => (
            <>
              <Cellule fort>{u.nom}<div className="text-xs font-normal text-[var(--muted)]">{u.role === "admin" ? "administrateur" : "assistant"} · {u.email}</div></Cellule>
              <Cellule><Badge ton={ETATS_PERSONNE[u.etat].ton}>{ETATS_PERSONNE[u.etat].label}</Badge></Cellule>
              <Cellule><Derniere iso={u.dernier_passage} /></Cellule>
              <Cellule droite>{u.telephones ? <Badge ton="vert"><BellRing size={11} className="inline -mt-0.5" /> {u.telephones} téléphone{u.telephones > 1 ? "s" : ""}</Badge> : <span className="text-[var(--muted)]">—</span>}</Cellule>
            </>
          )} />
      )}
      {groupe === "familles" && lignes.length > 300 && (
        <p className="text-xs text-[var(--muted)]">300 familles affichées sur {lignes.length} : affine la recherche.</p>
      )}
    </div>
  );
}

// ── Annonces & notifications ─────────────────────────────────────────────────────────

function Notifications() {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const { demander } = useConfirmation();
  const charger = () => api("/application/notifications").then(setD).catch((x) => setErreur(x.message));
  useEffect(() => { charger(); }, []);
  const essai = async () => {
    if (!await demander({ titre: "Envoyer une notification d'essai ?",
      texte: "Tous les téléphones de l'école abonnés aux notifications vont sonner.",
      valider: "Envoyer l'essai" })) return;
    try {
      const r = await api("/application/notifications/essai", { method: "POST" });
      setMessage(r.telephones ? `Essai envoyé à ${r.telephones} téléphone(s).` : "Aucun téléphone n'a reçu l'essai.");
      charger();
    } catch (x) { setErreur(x.message); }
  };
  return (
    <div className="space-y-10" data-testid="application-notifications">
      <AnnoncesApplication integre />
      <Section titre="Notifications sur les téléphones de l'école"
        aide="Le téléphone de la direction sonne comme pour un SMS quand quelque chose arrive. Les familles, elles, voient les annonces sur l'écran Accueil de leur app."
        testid="application-push"
        action={<button className="btn-ghost text-sm" onClick={essai} data-testid="application-push-essai"><BellRing size={14} /> Envoyer un essai</button>}>
        {erreur && <Erreur>{erreur}</Erreur>}
        {message && <p className="text-sm" style={{ color: "#15803D" }} data-testid="application-push-message">{message}</p>}
        {!d ? <Chargement /> : (
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 grid gap-4 content-start">
              <Encart titre="Ce qui fait sonner le téléphone" testid="application-push-declencheurs"
                aide="Exactement ce qu'« À traiter » liste, annoncé au plus toutes les 3 minutes.">
                <div className="flex flex-wrap gap-1.5">
                  {d.declencheurs.map((x) => <Badge key={x.titre} ton="bleu">{x.titre}</Badge>)}
                </div>
              </Encart>
              <Encart titre="Téléphones abonnés" testid="application-push-telephones"
                aide="Pour en ajouter un : dans l'app, mode école → Plus → Notifications → Activer.">
                {d.telephones.length === 0 ? <p className="text-sm text-[var(--muted)]">Aucun téléphone pour l'instant.</p> : (
                  <ul className="space-y-2.5">
                    {d.telephones.map((x, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "#EEF2FF", color: COULEUR_ECOLE }}>
                          <Smartphone size={15} />
                        </span>
                        <span className="min-w-0">
                          <span className="block font-semibold">{x.nom}</span>
                          <span className="block text-xs text-[var(--muted)]">{x.sujets.join(" · ")}{x.depuis ? ` · depuis le ${dateCH(x.depuis)}` : ""}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Encart>
            </div>
            <Encart titre="Dernières notifications parties" className="lg:col-span-3" testid="application-push-envois">
              {d.envois.length === 0 ? <p className="text-sm text-[var(--muted)]">Rien n'est encore parti (le journal est tenu depuis le 20.10.2026).</p> : (
                <ul className="divide-y" style={{ borderColor: "var(--border-soft)" }}>
                  {d.envois.map((x, i) => (
                    <li key={i} className="py-2.5 text-sm">
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold min-w-0 flex-1 truncate">{x.titre}</span>
                        <span className="text-xs text-[var(--muted)] whitespace-nowrap">{quand(x.le)}</span>
                      </div>
                      <div className="text-xs text-[var(--muted)]">{x.texte}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: x.telephones ? "#15803D" : "#B45309" }}>
                        {x.telephones ? `reçue sur ${x.telephones} téléphone(s)` : "aucun téléphone ne l'a reçue"}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Encart>
          </div>
        )}
      </Section>
    </div>
  );
}

// ── Historique ───────────────────────────────────────────────────────────────────────

const QUI = {
  famille: { label: "Familles", Icon: Users, couleur: T.accent, fond: T.doux },
  prof: { label: "Profs", Icon: Briefcase, couleur: COULEUR_PROFS, fond: TONS_BLOC.profs.doux },
  ecole: { label: "École", Icon: School, couleur: COULEUR_ECOLE, fond: TONS_BLOC.assistant.doux },
  simulation: { label: "Simulations", Icon: Eye, couleur: "#475569", fond: "#F1F5F9" },
};

function jourTitre(iso) {
  const d = new Date(iso);
  const jours = Math.round((Date.parse(jourZ(new Date())) - Date.parse(jourZ(d))) / 86400000);
  if (jours <= 0) return "Aujourd'hui";
  if (jours === 1) return "Hier";
  const t = d.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", timeZone: ZURICH });
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function Historique() {
  const [jours, setJours] = useState(30);
  const [qui, setQui] = useState("");
  const [q, setQ] = useState("");
  const [cherche, setCherche] = useState("");
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => { const m = setTimeout(() => setCherche(q), 300); return () => clearTimeout(m); }, [q]);
  useEffect(() => {
    setD(null);
    api(`/application/historique?jours=${jours}&qui=${qui}&cherche=${encodeURIComponent(cherche)}`)
      .then(setD).catch((x) => setErreur(x.message));
  }, [jours, qui, cherche]);
  const parJour = useMemo(() => {
    const groupes = [];
    for (const it of d?.items || []) {
      const cle = jourZ(new Date(it.le));
      if (!groupes.length || groupes[groupes.length - 1].cle !== cle) groupes.push({ cle, titre: jourTitre(it.le), items: [] });
      groupes[groupes.length - 1].items.push(it);
    }
    return groupes;
  }, [d]);

  return (
    <div className="space-y-5" data-testid="application-historique">
      <BarreFiltres>
        <Filtre label="Période">
          <Puces options={PERIODES.slice(0, 3)} valeur={jours} onChange={setJours} />
        </Filtre>
        <Filtre label="Qui">
          <Puces valeur={qui} onChange={setQui} testid="application-historique-qui"
            options={[{ valeur: "", label: "Tout" },
                      ...Object.entries(QUI).map(([k, v]) => ({ valeur: k, label: v.label, nb: d?.nombres?.[k], testid: `application-historique-${k}` }))]} />
        </Filtre>
        <div className="ml-auto"><Recherche valeur={q} onChange={setQ} largeur={240} testid="application-historique-recherche"
          placeholder="Famille, élève, cours…" /></div>
      </BarreFiltres>
      {erreur && <Erreur>{erreur}</Erreur>}
      {!d ? <Chargement /> : d.items.length === 0 ? (
        <p className="text-sm text-[var(--muted)] bg-white rounded-2xl border p-8 text-center" style={{ borderColor: "var(--border)" }}>
          Rien sur cette période.
        </p>
      ) : (
        <div className="space-y-5">
          {parJour.map((g) => (
            <section key={g.cle} data-testid="application-historique-jour">
              <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-2 px-1">{g.titre}</h3>
              <ul className="bg-white rounded-2xl border divide-y" style={{ borderColor: "var(--border)" }}>
                {g.items.map((it, i) => {
                  const c = QUI[it.qui] || QUI.famille;
                  return (
                    <li key={i} className="px-4 py-3 flex items-start gap-3 text-sm" data-testid="application-historique-ligne">
                      <span className="text-xs text-[var(--muted)] w-11 pt-1.5 shrink-0 tabular-nums">
                        {new Date(it.le).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit", timeZone: ZURICH })}
                      </span>
                      <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: c.fond, color: c.couleur }}>
                        <c.Icon size={15} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block">
                          <strong>{it.par}</strong>
                          {it.par_sous && <span className="text-xs text-[var(--muted)]"> ({it.par_sous})</span>}
                          {" — "}{it.action}
                        </span>
                        {(it.concerne || it.concerne_sous) && (
                          <span className="block text-xs mt-0.5">
                            {it.concerne}{it.concerne_sous && <span className="text-[var(--muted)]"> · {it.concerne_sous}</span>}
                          </span>
                        )}
                        {it.detail.map((x, j) => <span key={j} className="block text-xs text-[var(--muted)]">{x}</span>)}
                      </span>
                      {it.source && <span className="hidden sm:inline-block text-[11px] text-[var(--muted)] whitespace-nowrap pt-1.5">{it.source}</span>}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          {d.total > d.items.length && (
            <p className="text-xs text-[var(--muted)]">{d.items.length} lignes affichées sur {d.total} : réduis la période ou cherche un nom.</p>
          )}
        </div>
      )}
    </div>
  );
}
